import { useNavigate } from 'react-router';
import { ErrorPage } from '@screentone/addon-auth-wrapper';
import { Button, Group, Typography } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { useTimestampFormatters } from 'components/publish-history/hooks/useTimestampFormatters';
import { PageDto, ScheduledContentStatus, useGetLastNPublishedScheduledContentQuery } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { AuraEmployeeResponse, useAuraQuery } from 'hooks/useAuraQuery';
import { FOREVER_UTC } from 'utils/time';

interface VersionButtonProps {
  publishUtc: number;
  revisorDjUserId?: string | null;
  onClick?: () => void;
  disabled?: boolean;
}

const VersionButton = ({ publishUtc, revisorDjUserId, onClick, disabled }: VersionButtonProps) => {
  const currentProperty = useConvertedProperty();
  const { data: userData } = useAuraQuery(revisorDjUserId ?? '', {
    enabled: !!revisorDjUserId,
    currentProperty
  });
  const { formatTimestamp } = useTimestampFormatters();
  const employee = userData as AuraEmployeeResponse | undefined;
  const publisherName =
    employee?.first_name || employee?.last_name
      ? `${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim()
      : null;

  return (
    <Button tertiary onClick={onClick} disabled={disabled}>
      {formatTimestamp(publishUtc)}{publisherName ? ` — ${publisherName}` : ''}
    </Button>
  );
};

interface SectionPageVersionFallbackProps {
  allessehId: string | undefined;
  currentProperty: string | null | undefined;
  draftError?: unknown;
}

const SectionPageVersionFallback = ({ allessehId, currentProperty, draftError }: SectionPageVersionFallbackProps) => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetLastNPublishedScheduledContentQuery(
    {
      allessehId: allessehId ?? '',
      publishUtc: FOREVER_UTC,
      status: ScheduledContentStatus.Published,
      paginationParams: { first: 5, after: null, before: null }
    },
    { enabled: !!allessehId, retry: false }
  );

  if (!allessehId || !currentProperty) return <ErrorPage type="404" />;
  if (isLoading) return <ResponsiveLoader />;

  const versions = data?.lastNPublishedScheduledContent.edges ?? [];

  // Page has never been published and nothing errored — nothing to recover from
  if (!error && versions.length === 0) return <ErrorPage type="404" />;

  return (
    <Group
      direction="column"
      align="center"
      justify="center"
      gap="sm"
      style={{ minHeight: '60vh', padding: '48px 24px' }}
    >
      <Typography variant="header2">Unable to load this page</Typography>
      {draftError instanceof Error && draftError.message && (
        <Typography componentEl="p" size="sm" color="asphalt">
          {draftError.message}
        </Typography>
      )}
      {versions.length > 0 ? (
        <>
          <Typography componentEl="p" margin={{ bottom: 'md' }}>
            The current version had an error. Select a previous version to open it for review
            and click &ldquo;Save Draft&rdquo; to write over the current version.
          </Typography>
          {versions.map(({ node }) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if ((node.body as unknown) == null) {
              const {bodyError} = (node as unknown as { bodyError?: string });
              return (
                <Group key={node.publishUtc} direction="column" align="center" gap="xs">
                  <VersionButton
                    publishUtc={node.publishUtc}
                    revisorDjUserId={node.revisorUser?.djUserId}
                    disabled
                  />
                  {bodyError && (
                    <Typography componentEl="p" size="sm" color="asphalt">
                      {bodyError}
                    </Typography>
                  )}
                </Group>
              );
            }
            return (
              <VersionButton
                key={node.publishUtc}
                publishUtc={node.publishUtc}
                revisorDjUserId={(node.body as PageDto | undefined)?.metadata.revisorUser?.djUserId ?? node.revisorUser?.djUserId}
                onClick={() =>
                  navigate(`/${currentProperty}/pages/${allessehId}/version/${node.publishUtc}`, {
                    state: { historicalPageDto: node.body as PageDto }
                  })
                }
              />
            );
          })}
        </>
      ) : (
        <Typography componentEl="p">
          The current version had an error and previous versions could not be loaded. Please contact support.
        </Typography>
      )}
    </Group>
  );
};

export default SectionPageVersionFallback;
